export type ProcurementStatus = 'published' | 'in_review' | 'awarded' | 'cancelled' | 'closed'

export type ProcurementType = 'simple' | 'public'

export interface Attachment {
  id: number
  filename: string
  // Attachments are downloaded only through the backend's authenticated proxy.
  // The private object-store URL is intentionally not exposed by the API.
  downloadUrl: string
  contentType: string | null
  fileSize: number | null
  createdAt: string
}

export type SourceErrorKind = 'source_unavailable' | 'access_denied'

export interface ProcurementSourceRef {
  sourceId: number
  sourceName: string
  externalId: string
  tedId: string | null
  url: string | null
  // Set only on the detail payload, only for the ref matching this
  // procurement's own source, and only when the latest document-retrieval
  // attempt failed with a classified error. Null on every summary/list-view
  // ref and whenever the source is presumed reachable.
  errorKind: SourceErrorKind | null
}

export interface ProcurementSummary {
  id: number
  sourceId: number
  sourceName: string
  externalId: string
  title: string
  titleEng: string | null
  status: ProcurementStatus | null
  procurementType: ProcurementType | null
  // ISO 3166-1 alpha-3 country code of the contracting authority (e.g. "HRV", "DEU").
  // Populated for TED notices; null for domestic-only sources such as EOJN.
  country: string | null
  // Primary language the notice was published in (e.g. "hrv", "deu").
  // Populated for TED notices; null for domestic-only sources.
  sourceLanguage: string | null
  valueEur: number | null
  valueOriginal: number | null
  currency: string | null
  cpvCodes: string[]
  buyerName: string | null
  url: string | null
  sourceRefs: ProcurementSourceRef[]
  publishedAt: string | null
  deadlineAt: string | null
  aiScore: number | null
  // True the moment an analysis has ever been attempted, regardless of
  // outcome — unlike aiScore (only set once a match completes with a score),
  // this stays true for a failed or no-documents attempt too, so "Run AI"
  // can correctly become "Re-run AI" even when there's no score to show.
  aiAnalysisAttempted: boolean
  // True once this tender's submission deadline has passed. Drives the
  // Archive view and hides the Attachments section / AI-trigger button on
  // the detail page.
  archived: boolean
}

export type CriteriaMatchStatus = 'processing' | 'completed' | 'failed' | 'cancelled'

export type CriterionCategory =
  | 'technology'
  | 'company_certificate'
  | 'expert_certificate'
  | 'business_requirement'
  | 'experience_requirement'
  | 'staffing_requirement'
  | 'service_requirement'
  | 'domain_requirement'

export interface CriterionMatch {
  criterion: string
  category: CriterionCategory | string
  satisfied: boolean
  justification: string
  // Verbatim snippet the matcher copied from the company profile to support a
  // `satisfied` verdict; empty/absent when not satisfied. Optional because rows
  // written before this field existed carry no `evidence` key.
  evidence?: string
}

export type CriteriaErrorCode = 'rate_limited'

export interface CriteriaMatch {
  status: CriteriaMatchStatus
  matchPercentage: number | null
  requiredCriteria: unknown
  matchDetail: CriterionMatch[] | null
  model: string | null
  error: string | null
  // Coarse classification of `error` for a failed run: the source portal
  // returned 404/410 ('source_unavailable') or 401/403 ('access_denied').
  // Null for anything else (including a successful/no-documents result).
  errorKind: SourceErrorKind | null
  // Structured classification of `error`, separate from errorKind (which also
  // drives the Sources-section badge). Currently only 'rate_limited' (EOJN
  // HTTP 429); null for anything else. Lets the UI show one fixed translated
  // message instead of the raw error text.
  errorCode: CriteriaErrorCode | null
  // Technical sub-reason behind errorCode, e.g. 'recovery_budget_exhausted' or
  // 'cooldown_active'. Not translated — console-logged for debugging only,
  // never rendered.
  reason: string | null
  // Seconds-from-failure-time snapshot of the suggested retry delay, when known.
  retryAfterSeconds: number | null
  // Absolute deadline (failure time + retryAfterSeconds), computed once at
  // failure time. Use this (not retryAfterSeconds + updatedAt) to show the
  // true remaining wait, since it survives being viewed long after the
  // failure — hide the estimate entirely once it has passed.
  retryAfterUntil: string | null
  updatedAt: string
}

export interface AiScoreHistory {
  id: number
  matchPercentage: number
  requiredCriteria: unknown
  matchDetail: CriterionMatch[] | null
  model: string | null
  changeSummary: string | null
  changedTechnologies: string[]
  calculatedAt: string
  current: boolean
}

// AI-written summary ("AI description") + extracted key requirements, generated
// on demand alongside the criteria match. Reuses the same status vocabulary.
export interface AiAnalysis {
  status: CriteriaMatchStatus
  summary: string | null
  keyRequirements: string[] | null
  // English translations, written in the same analysis run. Null on rows
  // produced before translations existed; the UI falls back to the Croatian
  // fields when the active locale is English but these are missing.
  summaryEn: string | null
  keyRequirementsEn: string[] | null
  model: string | null
  error: string | null
  updatedAt: string
}

export interface ProcurementDetail extends ProcurementSummary {
  description: string | null
  titleEng: string | null
  // TED (Tenders Electronic Daily) reference. Present when a source exposed a
  // TED publication number; absent on rows that have not been matched to TED.
  tedId?: string | null
  // Free-text place-of-performance extracted from the notice. Populated for
  // TED notices when the source exposes it; null otherwise.
  placeOfPerformance: string | null
  originalUrl: string | null
  buyerId: string | null
  aiScoreReasons: string[] | null
  attachments: Attachment[]
  criteriaMatch: CriteriaMatch | null
  aiScoreHistory: AiScoreHistory[]
  aiAnalysis: AiAnalysis | null
  rawPayload: unknown
  createdAt: string
  updatedAt: string
}

export type ProcurementSortField = 'value' | 'type' | 'published' | 'deadline' | 'aiScore'

export type SortOrder = 'asc' | 'desc'

/** ISO 3166-1 alpha-3 codes that Argus ingests. Must stay in sync with ProcurementRoutes.ALLOWED_COUNTRIES. */
export const ALLOWED_COUNTRY_CODES = ['HRV', 'DEU', 'AUT', 'CHE'] as const
export type CountryCode = (typeof ALLOWED_COUNTRY_CODES)[number]

/** Currencies the value-range filter can be expressed in. Must stay in sync with ProcurementRoutes.ALLOWED_CURRENCIES. */
export const VALUE_RANGE_CURRENCIES = ['EUR', 'CHF'] as const
export type ValueRangeCurrency = (typeof VALUE_RANGE_CURRENCIES)[number]

export interface ProcurementFilters {
  search?: string
  sourceId?: number
  status?: ProcurementStatus
  procurementType?: ProcurementType
  // ISO 3166-1 alpha-3 country code to filter by contracting authority country.
  // 'HRV' also matches rows where country is null (EOJN domestic listings).
  country?: CountryCode
  // Svelte's bind:value on <input type="number"> sets this to `null` (not
  // `undefined`) when the field is cleared — callers building a request from
  // these filters must normalise via toScore() in composables/coerce.ts.
  minAiScore?: number | null
  cpv?: string
  createdSince?: string
  /** Feed-only recency: includes procurements whose AI score completed since this instant. */
  notificationSince?: string
  minValueEur?: number | null
  maxValueEur?: number | null
  // Currency minValueEur/maxValueEur are expressed in. Omitted (or 'EUR')
  // keeps the historic EUR-only behavior; the field names stay "...ValueEur"
  // for backward compatibility with existing saved filters/bookmarked URLs.
  currency?: ValueRangeCurrency
  sort?: ProcurementSortField
  order?: SortOrder
  limit?: number
  offset?: number
  // Opt-in: omitted leaves archive status unfiltered. true restricts to
  // tenders whose deadline has passed (the Archive view); false excludes
  // them (the Dashboard).
  archived?: boolean
  // The active CPV tender filter scoping "All Procurements" (see
  // stores/tenderFilter.ts). Omitted leaves it unfiltered, same contract as
  // archived above.
  filterId?: number
}

export type BulkDeleteItemStatus = 'deleted' | 'not_found'

export interface BulkDeleteItem {
  procurementId: number
  status: BulkDeleteItemStatus
  message: string | null
}

export interface BulkDeleteResult {
  results: BulkDeleteItem[]
}
